import { ArticleSource, NewsArticle } from '@/schema';
import type { TimeWindow } from '@/types';

import { request } from './client';

export const DEFAULT_NEWS_PAGE_SIZE = 6;

export interface FetchNewsFilters {
  symbols?: string[];
  sources?: ArticleSource[];
  from?: number;
  to?: number;
  searchQuery?: string;
  hasSymbolOnly?: boolean;
}

export interface FetchNewsParams {
  page?: number;
  limit?: number;
  filters?: FetchNewsFilters;
  signal?: AbortSignal;
}

export interface NewsResponse {
  news: NewsArticle[];
}

export interface NewsFrequencyPoint {
  date: string;
  count: number;
}

export interface NewsFrequencyResponse {
  frequency: NewsFrequencyPoint[];
  timeWindow?: TimeWindow;
}

export interface FetchNewsFrequencyParams {
  symbol: string;
  timeWindow: TimeWindow;
  from?: number;
  to?: number;
  timezone?: string;
  signal?: AbortSignal;
}

export interface FetchNewsArticleParams {
  id: string;
  signal?: AbortSignal;
}

export async function fetchNews({
  page = 1,
  limit = DEFAULT_NEWS_PAGE_SIZE,
  filters = {},
  signal,
}: FetchNewsParams = {}): Promise<NewsResponse> {
  const offset = (page - 1) * limit;

  const query = {
    limit,
    offset,
    symbols:
      filters.symbols && filters.symbols.length > 0
        ? filters.symbols.join(',')
        : undefined,
    sources:
      filters.sources && filters.sources.length > 0
        ? filters.sources.join(',')
        : undefined,
    from: filters.from,
    to: filters.to,
    q:
      filters.searchQuery && filters.searchQuery.length > 0
        ? filters.searchQuery
        : undefined,
    hasSymbol: filters.hasSymbolOnly ? 'true' : undefined,
  };

  return request<NewsResponse>({
    path: '/api/news',
    query,
    signal,
  });
}

export function fetchNewsArticle({ id, signal }: FetchNewsArticleParams) {
  return request<NewsArticle>({
    path: `/api/news/${encodeURIComponent(id)}`,
    signal,
  });
}

export function fetchNewsFrequency({
  symbol,
  timeWindow,
  from,
  to,
  timezone,
  signal,
}: FetchNewsFrequencyParams) {
  return request<NewsFrequencyResponse>({
    path: `/api/news/frequency/${encodeURIComponent(symbol)}`,
    query: {
      timeWindow,
      from,
      to,
      tz: timezone,
    },
    signal,
  });
}
